// Mock data for the "Cost Shift Grid" IFP report.
// Baseline cell values (columns 202623-202646) are transcribed directly from
// the reference design screenshots so the default-filter render matches
// exactly. Periods/columns beyond what the screenshots show (weeks 47-52)
// are extrapolated with the deterministic hash helpers below.

const ANCHOR_YEAR = 2026
const ROW_START_WEEK = 23
const ROW_END_WEEK = 34
const COLUMN_START_WEEK = 23
const COLUMN_END_WEEK = 52
// Highest week actually visible across both reference screenshots — anything
// past this is extrapolated rather than transcribed.
const TRANSCRIBED_MAX_WEEK = 46

function periodCode(week: number): string {
  return `${ANCHOR_YEAR}${String(week).padStart(2, '0')}`
}

export const ROW_PERIODS: string[] = Array.from(
  { length: ROW_END_WEEK - ROW_START_WEEK + 1 },
  (_, i) => periodCode(ROW_START_WEEK + i)
)

export const TO_PERIODS: string[] = Array.from(
  { length: COLUMN_END_WEEK - COLUMN_START_WEEK + 1 },
  (_, i) => periodCode(COLUMN_START_WEEK + i)
)

function weekOf(period: string): number {
  return parseInt(period.slice(4), 10)
}

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function normalizedFromHash(seed: number, salt: string): number {
  return (hashString(`${seed}:${salt}`) % 10000) / 10000
}

// Sparse baseline transcribed from cost-shift-grid-1.png / cost-shift-grid-2.png,
// keyed by `${from}-${to}`. Values not present here render as a blank cell
// (not `$0`) within the transcribed column range.
const BASELINE_MATRIX: Record<string, number> = {
  '202623-202623': 2439546,
  '202623-202624': 221575,
  '202623-202625': 53567,
  '202623-202626': 130065,
  '202623-202627': 2863,

  '202624-202623': 940163,
  '202624-202624': 2994118,
  '202624-202625': 1356265,
  '202624-202626': 324392,
  '202624-202627': 367907,
  '202624-202628': 856596,
  '202624-202629': 105391,
  '202624-202630': 6523,
  '202624-202631': 2163,
  '202624-202632': 4395,

  '202625-202623': 3648781,
  '202625-202624': 2085016,
  '202625-202625': 5791263,
  '202625-202626': 1041945,
  '202625-202627': 368983,
  '202625-202628': 38181,
  '202625-202629': 7224,
  '202625-202631': 323358,
  '202625-202634': 174720,

  '202626-202623': 1161205,
  '202626-202624': 2330384,
  '202626-202625': 4116091,
  '202626-202626': 1953613,
  '202626-202627': 1616254,
  '202626-202628': 125915,
  '202626-202629': 2896464,
  '202626-202630': 1545561,
  '202626-202631': 210643,
  '202626-202632': 95480,
  '202626-202633': 28928,
  '202626-202634': 143957,
  '202626-202636': 139344,
  '202626-202637': 219403,

  '202627-202623': 171700,
  '202627-202624': 920271,
  '202627-202625': 5219200,
  '202627-202626': 8001222,
  '202627-202627': 12631624,
  '202627-202628': 435394,
  '202627-202629': 158517,
  '202627-202630': 170991,
  '202627-202631': 32339,
  '202627-202632': 10545,

  '202628-202623': 717338,
  '202628-202624': 312745,
  '202628-202625': 1102049,
  '202628-202626': 832522,
  '202628-202627': 1390459,
  '202628-202628': 3936513,
  '202628-202629': 70625,
  '202628-202630': 36983,
  '202628-202631': 377150,
  '202628-202633': 98746,
  '202628-202634': 36058,
  '202628-202635': 19051,

  '202629-202627': 6218343,
  '202629-202628': 3274668,
  '202629-202629': 218584,

  '202630-202628': 5533489,
  '202630-202629': 914105,
  '202630-202630': 877746,

  '202631-202629': 9379065,
  '202631-202630': 1240451,
  '202631-202631': 3338423,

  '202632-202630': 26397948,
  '202632-202631': 2523012,
  '202632-202632': 2138608,
  '202632-202634': 69243,
  '202632-202635': 57202,
  '202632-202636': 20048,
  '202632-202638': 89600,

  '202633-202630': 57692,
  '202633-202631': 3692677,
  '202633-202632': 18822,
  '202633-202633': 2119305,
  '202633-202634': 36158,
  '202633-202636': 147417,

  '202634-202631': 117801,
  '202634-202632': 313650,
  '202634-202634': 5038553,
  '202634-202635': 64485,
  '202634-202636': 163358,
  '202634-202637': 56505,
  '202634-202638': 93525,
}

function baseValueFor(from: string, to: string): number | null {
  if (weekOf(to) <= TRANSCRIBED_MAX_WEEK) {
    return BASELINE_MATRIX[`${from}-${to}`] ?? null
  }
  return extendedValueFor(from, to)
}

// Extrapolated tail (weeks 47-52) — not visible in either screenshot.
// Kept sparse and decaying with distance from the diagonal, decaying faster
// to the left (acceleration) than to the right (delay), matching the visual
// emphasis of the "Delays" strip vs the "Accelerations" strip.
function extendedValueFor(from: string, to: string): number | null {
  const diff = weekOf(to) - weekOf(from)
  const seed = hashString(`ext:${from}:${to}`)
  const presenceThreshold =
    diff >= 0
      ? Math.max(0.1, 0.5 - diff * 0.02)
      : Math.max(0.04, 0.28 - Math.abs(diff) * 0.05)
  if (normalizedFromHash(seed, 'presence') > presenceThreshold) return null

  const magnitude = 20000 + normalizedFromHash(seed, 'magnitude') * 180000
  const decayRate = diff >= 0 ? 10 : 5
  const decay = Math.exp(-Math.abs(diff) / decayRate)
  return Math.round(magnitude * decay)
}

function unitCostFor(from: string, to: string): number {
  const seed = hashString(`cost:${from}:${to}`)
  return Math.round(8 + normalizedFromHash(seed, 'cost') * 32) // $8-$40
}

export const EVENT_OPTIONS: string[] = Array.from(
  { length: 10 },
  (_, i) => `SEA${String(i + 1).padStart(2, '0')}`
)

export const DC_OPTIONS = [
  '1_Dallas',
  '2_Atlanta',
  '3_Memphis',
  '4_Indianapolis',
  '5_Los Angeles',
]

export const VIEW_BY_OPTIONS = ['Inventory dollars', 'Units']

// Fixed anchor so the option list is stable across renders/sessions rather
// than depending on the actual current date.
const SNAPSHOT_ANCHOR = new Date('2026-07-14')

function lastFridayOnOrBefore(date: Date): Date {
  const next = new Date(date)
  const day = next.getDay() // 0=Sun...6=Sat, Fri=5
  const diff = (day - 5 + 7) % 7
  next.setDate(next.getDate() - diff)
  return next
}

function formatSnapshotDate(date: Date): string {
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
}

const RECENT_FRIDAYS: string[] = (() => {
  const fridays: string[] = []
  let cursor = lastFridayOnOrBefore(SNAPSHOT_ANCHOR)
  for (let i = 0; i < 4; i++) {
    fridays.push(formatSnapshotDate(cursor))
    cursor = new Date(cursor)
    cursor.setDate(cursor.getDate() - 7)
  }
  return fridays
})()

export const SNAPSHOT_DATE_OPTIONS = ['Latest', ...RECENT_FRIDAYS]

export interface CostShiftFilterState {
  snapshotDate: string
  season: string | null
  event: string | null
  dc: string | null
  viewBy: string
  divisionId: string | null
  departmentId: string | null
  classId: string | null
  subclassId: string | null
}

export interface CostShiftCell {
  from: string
  to: string
  value: number | null
}

function shrinkForFilter(name: string, value: string): number {
  const norm = normalizedFromHash(hashString(`${name}:${value}`), 'shrink')
  return 0.4 + norm * 0.35 // [0.4, 0.75]
}

function cosmeticFilterPairs(
  filters: CostShiftFilterState
): Array<[string, string]> {
  const pairs: Array<[string, string]> = []
  if (filters.season) pairs.push(['season', filters.season])
  if (filters.event) pairs.push(['event', filters.event])
  if (filters.dc) pairs.push(['dc', filters.dc])
  if (filters.divisionId) pairs.push(['division', filters.divisionId])
  if (filters.departmentId) pairs.push(['department', filters.departmentId])
  if (filters.classId) pairs.push(['class', filters.classId])
  if (filters.subclassId) pairs.push(['subclass', filters.subclassId])
  if (filters.snapshotDate !== 'Latest')
    pairs.push(['snapshotDate', filters.snapshotDate])
  return pairs
}

export function buildCostShiftMatrix(
  filters: CostShiftFilterState
): CostShiftCell[][] {
  const pairs = cosmeticFilterPairs(filters)
  const shrink = pairs.reduce(
    (acc, [name, value]) => acc * shrinkForFilter(name, value),
    1
  )

  return ROW_PERIODS.map(from =>
    TO_PERIODS.map(to => {
      const raw = baseValueFor(from, to)
      if (raw == null) return { from, to, value: null }

      const shrunk = Math.max(1, Math.round(raw * shrink))
      if (filters.viewBy === 'Units') {
        const cost = unitCostFor(from, to)
        return { from, to, value: Math.max(1, Math.round(shrunk / cost)) }
      }
      return { from, to, value: shrunk }
    })
  )
}

export function formatCostShiftValue(value: number, viewBy: string): string {
  if (viewBy === 'Units') return value.toLocaleString('en-US')
  return `$${value.toLocaleString('en-US')}`
}
